import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import { usePopup } from '../../common/Popup';
import PasswordInput from '../../common/PasswordInput';

const SquareContainer = styled.div`
  width: 90%;
  max-width: 450px;
  min-height: 420px;
  background: rgb(255, 255, 255);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 28px;
  padding: 40px;
  box-shadow: 0px 4px #00000040;
  z-index: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 95%;
    max-width: none;
    padding: 30px 20px;
    min-height: auto;
  }

  @media (max-width: 480px) {
    padding: 25px 15px;
    border-radius: 20px;
  }
`;

const InputGroup = styled.div`
  width: 80%;
  margin-bottom: 10px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    margin-bottom: 15px;
  }
`;

const Label = styled.label`
  font-size: 14px;
  color: #002E3B;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: left;
  font-family: "Public Sans";

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 6px;
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 12px;
  border: 0.5px solid #002E3B;
  border-radius: 9px;
  font-size: 14px;
  font-family: "Public Sans";
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #6AB8A8;
    box-shadow: 0 0 0 2px rgba(106, 184, 168, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    font-size: 12px;
  }
`;

const ForgotPassword = styled.a`
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  text-decoration: none;
  color: #269DF2;
  font-size: 13px;
  font-family: "Public Sans";
  cursor: pointer;
  text-align: right;
  margin-bottom: 30px;
  
  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 25px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: auto;

  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const BaseButton = styled.button`
  width: 100%;
  max-width: 70%;
  height: 40px;
  border: none;
  outline: none;
  border-radius: 9px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.223);
  cursor: pointer;
  font-size: 15px;
  color: #ffffff;
  font-weight: 700;
  font-family: "Public Sans";
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0px);
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.223);
  }

  @media (max-width: 768px) {
    height: 42px;
    font-size: 14px;
    max-width: 70%;
  }

  @media (max-width: 480px) {
    height: 40px;
    font-size: 13px;
    max-width: 80%;
  }
`;

const LoginButton = styled(BaseButton)`
  background-color: #FF9F1F;

  &:hover {
    background-color: #e8900d;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.223);
  }
`;

const CadastroButton = styled(BaseButton)`
  background-color: #6AB8A8;

  &:hover {
    background-color: #5aa396;
  }
`;

const SuccessMessage = styled.p`
  color: #28a745;
  font-size: 13px;
  font-family: "Public Sans";
  text-align: center;
  margin-top: 15px;
  padding: 8px;

  @media (max-width: 480px) {
    font-size: 12px;
    margin-top: 10px;
    padding: 6px;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoginSquare = () => {
  const navigate = useNavigate();
  const popup = usePopup();
  const [emailCpf, setEmailCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!emailCpf || !senha) {
      await popup.alert({
        icon: 'warning',
        message: 'Por favor, preencha todos os campos',
      });
      return;
    }

    setLoading(true);
    setSucesso('');

    try {
      const response = await apiService.login({ emailCpf, senha });
      
      if (response.status === 'CADASTRO') {
        localStorage.setItem('userId', response.usuarioId);
        localStorage.setItem('token', response.api_token);
        if (response.refreshTokenHandle) {
          localStorage.setItem('refreshTokenHandle', response.refreshTokenHandle);
        }

        setSucesso(response.message || 'Complete seu cadastro para continuar');
        setTimeout(() => {
          navigate(response.rota || '/PreenchaDados');
        }, 1000);
        return;
      }

      if (response.status === 'PENDENTE') {
        await popup.alert({
          icon: 'warning',
          message: 'Seu cadastro está aguardando aprovação pelo administrador.',
        });
        setLoading(false);
        return;
      }

      if (response.status === 'INATIVO') {
        await popup.alert({
          icon: 'warning',
          message: 'Seu cadastro está inativo, contate o administrador.',
        });
        setLoading(false);
        return;
      }

      if (response.status !== 'ATIVO') {
        await popup.alert({
          icon: 'warning',
          message: `Status de usuário não reconhecido: ${response.status}`,
        });
        setLoading(false);
        return;
      }

      localStorage.setItem('token', response.api_token);
      if (response.refreshTokenHandle) {
        localStorage.setItem('refreshTokenHandle', response.refreshTokenHandle);
      }
      localStorage.setItem('user', JSON.stringify({
        id: response.id,
        nome: response.nome,
        email: response.email,
        cpf: response.cpf,
        cargo: response.cargo,
        cargoId: response.cargoId,
      }));

      setSucesso('Login realizado com sucesso!');
      
      if (response.cargoId === '4') {
        setTimeout(() => {
          navigate('/DashboardES');
        }, 1000)
      } else {
        setTimeout(() => {
          navigate('/DashboardInicio');
        }, 1000);
      }

    } catch (error) {
      console.error('Erro no login:', error);
      await popup.alert({
        icon: 'error',
        message: error.message || 'Usuário ou senha inválidos',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleLogin();
    }
  };

  return (
    <SquareContainer>
      <InputGroup>
        <Label htmlFor="emailCpf">Email / CPF</Label>
        <InputField
          id="emailCpf"
          type="text"
          value={emailCpf}
          onChange={(e) => setEmailCpf(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          required
        />
      </InputGroup>

      <InputGroup>
        <Label htmlFor="senha">Senha</Label>
        <PasswordInput
          inputAs={InputField}
          id="senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          required
        />
      </InputGroup>

      <ForgotPassword href="/esqueci-senha">
        Esqueceu sua senha?
      </ForgotPassword>

      <ButtonContainer>
        <LoginButton 
          type="button" 
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? <LoadingSpinner /> : 'Entrar'}
        </LoginButton>

        <CadastroButton 
          type="button" 
          onClick={() => navigate('/Cadastro')}
          disabled={loading}
        >
          Não tenho cadastro
        </CadastroButton>
      </ButtonContainer>

      {sucesso && <SuccessMessage>{sucesso}</SuccessMessage>}
    </SquareContainer>
  );
};

export default LoginSquare;